import {
  masterParamPayloadSchema,
  MasterParamPayloadSchema,
  useMasterParam,
  useMasterParamEdit,
} from "@/api/master-param";
import { FormContainer } from "@/components/form/form-container";
import { Loading } from "@/components/ui/loading";
import { swalError, swalSuccess } from "@/utils/lib/swal";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { MasterParamFormFields } from "./MasterParamFormFields";
import { SubmitButton } from "@/components/ui/button";
import { Anchor } from "@/components/ui/anchor";
import { AxiosError } from "axios";

export function MasterParamEditForm({ id }: { id: string }) {
  const { data: masterParam, isPending: isLoadingData } = useMasterParam({
    id,
  });

  const form = useForm<MasterParamPayloadSchema>({
    resolver: zodResolver(masterParamPayloadSchema),
    defaultValues: {
      paramType: masterParam?.paramType,
      paramTag: masterParam?.paramTag,
      value: masterParam?.value,
      active: masterParam?.active,
      value2: masterParam?.value2,
      description: masterParam?.description,
    },
    values: {
      paramType: masterParam?.paramType ?? "",
      paramTag: masterParam?.paramTag ?? "",
      value: masterParam?.value ?? "",
      active: masterParam?.active ? "true" : "false",
      value2: masterParam?.value2 ?? "",
      description: masterParam?.description ?? "",
    },
  });

  const { mutate, isPending } = useMasterParamEdit();
  const navigate = useNavigate();

  const handleSubmit = (values: MasterParamPayloadSchema) => {
    if (values.active === "true") {
      values.active = true;
    } else {
      values.active = false;
    }

    mutate(
      { id: id, data: values },
      {
        onSuccess: () => {
          swalSuccess
            .fire({
              text: "Master Param has been updated",
            })
            .then(() => navigate("/master-data/master-param"));
        },
        onError: (error: Error) => {
          const err = error as AxiosError;
          const errorMessage = (err.response?.data as { data: unknown })?.data;

          swalError.fire({
            text: `${errorMessage}`,
          });
        },
      },
    );
  };

  if (isLoadingData) {
    return <Loading />;
  }

  return (
    <FormContainer form={form} onSubmit={handleSubmit}>
      <MasterParamFormFields />
      <div className="ml-auto flex gap-x-2">
        <Anchor href="/master-data/master-param" mood="axiata-blue-outline">
          Cancel
        </Anchor>
        <SubmitButton loading={isPending} />
      </div>
    </FormContainer>
  );
}
